(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-boho-shop-look]';


  class BohoShopTheLook {

    constructor(root) {
      this.root = root;

      this.isCustomizing = false;
      this.fillerDraft = new Map();
      this.items = [];

      this.cacheElements();
      this.createItemState();
      this.bindEvents();
      this.setupStickyCartObserver();
      this.enableDesktopScrollHandoff();
      this.renderAll();
    }


    /* ============================================================
       CACHE
       ============================================================ */

    cacheElements() {

      this.productList =
        this.root.querySelector('[data-look-items]');

      this.shopPanelHeading =
        this.root.querySelector('[data-shop-panel-heading]');


      /* Customize */

      this.customizeLookButton =
        this.root.querySelector('[data-customize-look]');


      /* Fillers */

      this.fillerCard =
        this.root.querySelector('[data-fillers]');

      this.fillerToggle =
        this.root.querySelector('[data-filler-toggle]');

      this.fillerAvailable =
        this.root.querySelector('[data-filler-available]');

      this.fillerAdded =
        this.root.querySelector('[data-filler-added]');

      this.fillerSelectedCount =
        this.root.querySelector('[data-filler-selected-count]');

      this.fillerSelectedLabel =
        this.root.querySelector('[data-filler-selected-label]');

      this.fillerCardPrice =
        this.root.querySelector('[data-filler-card-price]');

      this.fillerTitleDefault =
        this.root.querySelector('[data-filler-title-default]');

      this.fillerTitleAdded =
        this.root.querySelector('[data-filler-title-added]');

      this.customizeFillersButton =
        this.root.querySelector('[data-customize-fillers]');


      /* Summary */

      this.pieceCount =
        this.root.querySelector('[data-total-pieces]');

      this.pieceLabel =
        this.root.querySelector('[data-piece-label]');

      this.totalCompare =
        this.root.querySelector('[data-total-compare]');

      this.totalPrice =
        this.root.querySelector('[data-total-price]');

      this.savingRow =
        this.root.querySelector('[data-saving-row]');

      this.totalSavings =
        this.root.querySelector('[data-total-savings]');

      this.discountPercent =
        this.root.querySelector('[data-discount-percent]');

      this.addPrice =
        this.root.querySelector('[data-add-price]');

      this.addButton =
        this.root.querySelector('[data-add-look]');

      this.addLabel =
        this.root.querySelector('[data-add-label]');

      this.cartMessage =
        this.root.querySelector('[data-cart-message]');


      /* Sticky cart */

      this.stickyCart =
        this.root.querySelector('[data-sticky-cart]');

      this.stickyPieces =
        this.root.querySelector('[data-sticky-pieces]');

      this.stickyPieceLabel =
        this.root.querySelector('[data-sticky-piece-label]');

      this.stickyCompare =
        this.root.querySelector('[data-sticky-compare]');

      this.stickyPrice =
        this.root.querySelector('[data-sticky-price]');

      this.stickySaving =
        this.root.querySelector('[data-sticky-saving]');

      this.stickyDiscount =
        this.root.querySelector('[data-sticky-discount]');

      this.stickyAddButton =
        this.root.querySelector('[data-sticky-add]');


      /* Filler modal */

      this.fillerModal =
        this.root.querySelector('[data-filler-modal]');

      this.fillerModalItems =
        this.root.querySelector('[data-filler-modal-items]');

      this.saveFillersButton =
        this.root.querySelector('[data-save-fillers]');

      this.modalFillerCount =
        this.root.querySelector('[data-modal-filler-count]');

      this.modalFillerLabel =
        this.root.querySelector('[data-modal-filler-label]');

      this.modalFillerPrice =
        this.root.querySelector('[data-modal-filler-price]');
    }


    /* ============================================================
       CREATE ITEM STATE
       ============================================================ */

    createItemState() {

      const elements =
        Array.from(
          this.root.querySelectorAll('[data-look-item]')
        );


      this.items = elements.map((element) => {

        const originalQuantity =
          Math.max(
            1,
            Number(
              element.dataset.originalQuantity || 1
            )
          );


        const unitPrice =
          Number(
            element.dataset.unitPrice || 0
          );


        const comparePrice =
          Number(
            element.dataset.comparePrice ||
            unitPrice ||
            0
          );


        const fillerPrice =
          Number(
            element.dataset.fillerPrice || 0
          );


        const fillerComparePrice =
          Number(
            element.dataset.fillerComparePrice ||
            fillerPrice ||
            0
          );


        return {

          element,

          variantId:
            Number(
              element.dataset.variantId || 0
            ),

          unitPrice,
          comparePrice,

          originalQuantity,

          quantity:
            originalQuantity,

          included:
            element.dataset.included !== 'false',

          hasFiller:
            element.dataset.hasFiller === 'true',

          fillerVariantId:
            Number(
              element.dataset.fillerVariantId || 0
            ),

          fillerPrice,
          fillerComparePrice,

          fillerQuantity:
            Math.max(
              0,
              Number(
                element.dataset.fillerQuantity || 0
              )
            )

        };

      });
    }


    /* ============================================================
       HELPERS
       ============================================================ */

    wait(milliseconds = 180) {

      return new Promise((resolve) => {

        window.setTimeout(
          resolve,
          milliseconds
        );

      });
    }


    escapeAttribute(value) {

      return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('"', '&quot;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;');
    }


    formatMoney(cents) {

      if (
        window.Shopify &&
        typeof window.Shopify.formatMoney === 'function'
      ) {

        return window.Shopify.formatMoney(
          cents,
          this.root.dataset.moneyFormat || undefined
        );

      }


      const currency =
        window.Shopify?.currency?.active ||
        this.root.dataset.currency ||
        'INR';


      return new Intl.NumberFormat(
        document.documentElement.lang || 'en-IN',
        {
          style: 'currency',
          currency,
          maximumFractionDigits: 0
        }
      ).format(
        Number(cents || 0) / 100
      );
    }


    lockPage() {

      document.documentElement.style.overflow =
        'hidden';
    }


    unlockPage() {

      document.documentElement.style.overflow =
        '';
    }


    /* ============================================================
       BUTTON LOADERS
       ============================================================ */

    setButtonLoading(
      button,
      loading,
      loadingLabel = ''
    ) {

      if (!button) {
        return;
      }


      const loader =
        button.querySelector(
          '[data-button-loader]'
        );


      const label =
        button.querySelector(
          '[data-button-label]'
        );


      if (loading) {

        button.disabled = true;

        button.classList.add(
          'is-loading'
        );


        if (loader) {
          loader.hidden = false;
        }


        if (
          label &&
          loadingLabel
        ) {

          if (!label.dataset.originalLabel) {

            label.dataset.originalLabel =
              label.textContent.trim();

          }


          label.textContent =
            loadingLabel;

        }

      } else {

        button.disabled = false;

        button.classList.remove(
          'is-loading'
        );


        if (loader) {
          loader.hidden = true;
        }


        if (
          label &&
          label.dataset.originalLabel
        ) {

          label.textContent =
            label.dataset.originalLabel;


          delete label.dataset.originalLabel;

        }

      }
    }


    async withButtonLoader(
      button,
      callback,
      loadingLabel = ''
    ) {

      if (!button) {
        return;
      }


      if (
        button.classList.contains(
          'is-loading'
        )
      ) {
        return;
      }


      this.setButtonLoading(
        button,
        true,
        loadingLabel
      );


      try {

        await callback();

      } finally {

        this.setButtonLoading(
          button,
          false
        );

      }
    }


    /* ============================================================
       ITEM QUANTITY
       ============================================================ */

    getActiveQuantity(item) {

      return item.included
        ? item.quantity
        : 0;
    }


    setIncluded(
      item,
      included
    ) {

      item.included =
        Boolean(included);


      if (item.included) {

        item.quantity =
          item.originalQuantity;

      } else {

        item.fillerQuantity =
          0;

      }


      this.normalizeFillers();
      this.renderAll();
    }


    changeQuantity(
      item,
      delta
    ) {

      if (
        !this.isCustomizing ||
        !item.included
      ) {
        return;
      }


      const next =
        item.quantity +
        delta;


      if (next <= 0) {

        item.included =
          false;

        item.fillerQuantity =
          0;


        this.normalizeFillers();
        this.renderAll();

        return;
      }


      item.quantity =
        Math.min(
          next,
          10
        );


      this.normalizeFillers();
      this.renderAll();
    }


    async enableCustomizeLook() {

      if (this.isCustomizing) {
        return;
      }


      this.isCustomizing =
        true;


      this.root.classList.add(
        'is-customizing'
      );


      if (this.customizeLookButton) {

        this.customizeLookButton.hidden =
          true;

      }


      if (this.productList) {

        this.productList.scrollTo({
          top: 0,
          behavior: 'smooth'
        });

      }


      await this.wait(40);


      if (this.shopPanelHeading) {

        this.shopPanelHeading.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });

      }


      await this.wait(350);
    }


    /* ============================================================
       FILLERS
       ============================================================ */

    getFillerMax(item) {

  if (
    !item.hasFiller ||
    !item.included
  ) {
    return 0;
  }

  return item.quantity;
}


    getFillerItems() {

      return this.items.filter(
        (item) =>
          item.hasFiller
      );
    }


    getActiveFillerItems() {

      return this
        .getFillerItems()
        .filter(
          (item) =>
            this.getFillerMax(item) > 0
        );
    }


    normalizeFillers() {

      this.getFillerItems().forEach(
        (item) => {

          const max =
            this.getFillerMax(item);


          item.fillerQuantity =
            Math.max(
              0,
              Math.min(
                item.fillerQuantity,
                max
              )
            );

        }
      );
    }


    getSelectedFillerCount() {

      return this
        .getFillerItems()
        .reduce(
          (total, item) =>
            total +
            item.fillerQuantity,
          0
        );
    }


    getSelectedFillerPrice() {

      return this
        .getFillerItems()
        .reduce(
          (total, item) =>
            total +
            (
              item.fillerQuantity *
              item.fillerPrice
            ),
          0
        );
    }


    getSelectedFillerComparePrice() {

      return this
        .getFillerItems()
        .reduce(
          (total, item) =>
            total +
            (
              item.fillerQuantity *
              Math.max(
                item.fillerComparePrice,
                item.fillerPrice
              )
            ),
          0
        );
    }


    getFullFillerPrice() {

      return this
        .getActiveFillerItems()
        .reduce(
          (total, item) =>
            total +
            (
              this.getFillerMax(item) *
              item.fillerPrice
            ),
          0
        );
    }


    addAllFillers() {

      this.getFillerItems().forEach(
        (item) => {

          item.fillerQuantity =
            this.getFillerMax(item);

        }
      );


      this.renderAll();
    }


    removeAllFillers() {

      this.getFillerItems().forEach(
        (item) => {

          item.fillerQuantity =
            0;

        }
      );


      this.renderAll();
    }


    async handleFillerToggle() {

      if (!this.fillerToggle) {
        return;
      }


      this.fillerToggle.disabled =
        true;


      try {

        if (
          this.fillerToggle.checked
        ) {

          this.addAllFillers();

        } else {

          this.removeAllFillers();

        }


        await this.wait(120);

      } finally {

        this.fillerToggle.disabled =
          false;

      }
    }


    /* ============================================================
       TOTALS
       ============================================================ */

    calculateTotals() {

      let pieces = 0;

      let merchandiseSelling = 0;
      let merchandiseCompare = 0;


      this.items.forEach(
        (item) => {

          const quantity =
            this.getActiveQuantity(item);


          pieces +=
            quantity;


          merchandiseSelling +=
            quantity *
            item.unitPrice;


          merchandiseCompare +=
            quantity *
            Math.max(
              item.comparePrice,
              item.unitPrice
            );

        }
      );


      const fillerSelling =
        this.getSelectedFillerPrice();


      const fillerCompare =
        this.getSelectedFillerComparePrice();


      const totalSelling =
        merchandiseSelling +
        fillerSelling;


      const totalCompare =
        merchandiseCompare +
        fillerCompare;


      const savings =
        Math.max(
          0,
          totalCompare -
          totalSelling
        );


      const discountPercent =
        totalCompare > 0 &&
        savings > 0

          ? Math.round(
              (
                savings /
                totalCompare
              ) * 100
            )

          : 0;


      return {
        pieces,
        totalSelling,
        totalCompare,
        savings,
        discountPercent
      };
    }


    /* ============================================================
       RENDER ITEM
       ============================================================ */

    renderItem(item) {

      const element =
        item.element;


      const checkbox =
        element.querySelector(
          '[data-item-checkbox]'
        );


      const qtyCopy =
        element.querySelector(
          '[data-qty-copy]'
        );


      const qtyValue =
        element.querySelector(
          '[data-quantity-value]'
        );


      const linePrice =
        element.querySelector(
          '[data-line-price]'
        );


      const lineCompare =
        element.querySelector(
          '[data-line-compare]'
        );


      if (checkbox) {

        checkbox.checked =
          item.included;

      }


      if (qtyCopy) {

        qtyCopy.textContent =
          item.included
            ? item.quantity
            : 0;

      }


      if (qtyValue) {

        qtyValue.textContent =
          item.quantity;

      }


      if (linePrice) {

        linePrice.textContent =
          item.included

            ? this.formatMoney(
                item.unitPrice *
                item.quantity
              )

            : 'Removed';

      }


      if (lineCompare) {

        lineCompare.textContent =
          item.included

            ? this.formatMoney(
                item.comparePrice *
                item.quantity
              )

            : '';

      }


      element.classList.toggle(
        'is-excluded',
        !item.included
      );
    }


    /* ============================================================
       RENDER FILLER CARD
       ============================================================ */

    renderFillerCard() {

      if (!this.fillerCard) {
        return;
      }


      this.normalizeFillers();


      const capacity =
        this
          .getActiveFillerItems()
          .reduce(
            (total, item) =>
              total +
              this.getFillerMax(item),
            0
          );


      const selected =
        this.getSelectedFillerCount();


      const selectedPrice =
        this.getSelectedFillerPrice();


      this.fillerCard.hidden =
        capacity === 0;


      if (capacity === 0) {
        return;
      }


      if (
        this.fillerSelectedCount
      ) {

        this.fillerSelectedCount.textContent =
          selected;

      }


      if (
        this.fillerSelectedLabel
      ) {

        this.fillerSelectedLabel.textContent =
          selected === 1
            ? 'filler'
            : 'fillers';

      }


      if (this.fillerToggle) {

        this.fillerToggle.checked =
          selected > 0;

        this.fillerToggle.indeterminate =
          false;

      }


      if (this.fillerAvailable) {

        this.fillerAvailable.hidden =
          selected > 0;

      }


      if (this.fillerAdded) {

        this.fillerAdded.hidden =
          selected === 0;

      }


      if (
        this.fillerTitleDefault
      ) {

        this.fillerTitleDefault.hidden =
          selected > 0;

      }


      if (
        this.fillerTitleAdded
      ) {

        this.fillerTitleAdded.hidden =
          selected === 0;

      }


      if (this.fillerCardPrice) {

        this.fillerCardPrice.textContent =
          selected > 0

            ? `+ ${this.formatMoney(
                selectedPrice
              )}`

            : `+ ${this.formatMoney(
                this.getFullFillerPrice()
              )}`;

      }
    }


    /* ============================================================
       RENDER SUMMARY
       ============================================================ */

    renderSummary() {

      const totals =
        this.calculateTotals();


      if (this.pieceCount) {

        this.pieceCount.textContent =
          totals.pieces;

      }


      if (this.pieceLabel) {

        this.pieceLabel.textContent =
          totals.pieces === 1
            ? 'item included'
            : 'items included';

      }


      if (this.totalPrice) {

        this.totalPrice.textContent =
          this.formatMoney(
            totals.totalSelling
          );

      }


      if (this.addPrice) {

        this.addPrice.textContent =
          this.formatMoney(
            totals.totalSelling
          );

      }


      const hasSaving =
        totals.savings > 0 &&
        totals.totalCompare >
        totals.totalSelling;


      if (this.totalCompare) {

        this.totalCompare.hidden =
          !hasSaving;


        if (hasSaving) {

          this.totalCompare.textContent =
            this.formatMoney(
              totals.totalCompare
            );

        }

      }


      if (this.savingRow) {

        this.savingRow.hidden =
          !hasSaving;

      }


      if (
        hasSaving &&
        this.totalSavings
      ) {

        this.totalSavings.textContent =
          this.formatMoney(
            totals.savings
          );

      }


      if (
        hasSaving &&
        this.discountPercent
      ) {

        this.discountPercent.textContent =
          totals.discountPercent;

      }


      if (this.addButton) {

        this.addButton.disabled =
          totals.pieces === 0;

      }


      if (this.addLabel) {

        this.addLabel.textContent =
          totals.pieces === 0
            ? 'Select a piece'
            : 'Add to cart';

      }
    }


    /* ============================================================
       RENDER STICKY
       ============================================================ */

    renderStickyCart() {

      if (!this.stickyCart) {
        return;
      }


      const totals =
        this.calculateTotals();


      if (this.stickyPieces) {

        this.stickyPieces.textContent =
          totals.pieces;

      }


      if (
        this.stickyPieceLabel
      ) {

        this.stickyPieceLabel.textContent =
          totals.pieces === 1
            ? 'item'
            : 'items';

      }


      if (this.stickyPrice) {

        this.stickyPrice.textContent =
          this.formatMoney(
            totals.totalSelling
          );

      }


      const hasSaving =
        totals.savings > 0 &&
        totals.totalCompare >
        totals.totalSelling;


      if (this.stickyCompare) {

        this.stickyCompare.hidden =
          !hasSaving;


        if (hasSaving) {

          this.stickyCompare.textContent =
            this.formatMoney(
              totals.totalCompare
            );

        }

      }


      if (this.stickySaving) {

        this.stickySaving.hidden =
          !hasSaving;

      }


      if (
        hasSaving &&
        this.stickyDiscount
      ) {

        this.stickyDiscount.textContent =
          totals.discountPercent;

      }


      if (this.stickyAddButton) {

        this.stickyAddButton.disabled =
          totals.pieces === 0;

      }
    }


    renderAll() {

      this.normalizeFillers();


      this.items.forEach(
        (item) => {

          this.renderItem(item);

        }
      );


      this.renderFillerCard();
      this.renderSummary();
      this.renderStickyCart();
    }


    /* ============================================================
       FILLER MODAL
       ============================================================ */

    updateModalSummary() {

      let count = 0;
      let total = 0;


      this.fillerDraft.forEach(
        (quantity, item) => {

          count += quantity;

          total +=
            quantity *
            item.fillerPrice;

        }
      );


      if (this.modalFillerCount) {

        this.modalFillerCount.textContent =
          count;

      }


      if (this.modalFillerLabel) {

        this.modalFillerLabel.textContent =
          count === 1
            ? 'filler'
            : 'fillers';

      }


      if (this.modalFillerPrice) {

        this.modalFillerPrice.textContent =
          this.formatMoney(total);

      }
    }


    buildFillerModal() {

      if (!this.fillerModalItems) {
        return;
      }


      this.fillerModalItems.innerHTML =
        '';


      this.fillerDraft =
        new Map();


      this
        .getActiveFillerItems()
        .forEach(
          (item) => {

            const max =
              this.getFillerMax(item);


            const current =
              Math.min(
                item.fillerQuantity,
                max
              );


            this.fillerDraft.set(
              item,
              current
            );


            const title =
              item.element
                .querySelector(
                  '[data-item-title]'
                )
                ?.textContent
                ?.trim() ||
              'Cushion cover';


            const detail =
              item.element
                .querySelector(
                  '[data-item-detail]'
                )
                ?.textContent
                ?.trim() ||
              '';


            const image =
              item.element
                .querySelector(
                  '[data-item-image]'
                );


            const imageSrc =
              image?.currentSrc ||
              image?.src ||
              '';


            const row =
              document.createElement(
                'div'
              );


            row.className =
              'boho-filler-row';


            row.innerHTML = `
              <div class="boho-filler-row__image">
                ${
                  imageSrc
                    ? `<img src="${this.escapeAttribute(imageSrc)}" alt="">`
                    : ''
                }
              </div>

              <div class="boho-filler-row__content">

                <p class="boho-filler-row__title"></p>

                <p class="boho-filler-row__detail"></p>

                <p class="boho-filler-row__max">
                  Max ${max}
                </p>

              </div>

              <div class="boho-filler-row__controls-wrap">

                <div class="boho-filler-row__controls">

                  <button
                    type="button"
                    class="boho-filler-row__button"
                    data-filler-minus
                    aria-label="Decrease filler quantity"
                  >
                    −
                  </button>

                  <span
                    class="boho-filler-row__value"
                    data-filler-value
                  >
                    0
                  </span>

                  <button
                    type="button"
                    class="boho-filler-row__button"
                    data-filler-plus
                    aria-label="Increase filler quantity"
                  >
                    +
                  </button>

                </div>

                <p
                  class="boho-filler-row__price"
                  data-filler-row-price
                ></p>

              </div>
            `;


            row.querySelector(
              '.boho-filler-row__title'
            ).textContent =
              title;


            row.querySelector(
              '.boho-filler-row__detail'
            ).textContent =
              detail;


            const value =
              row.querySelector(
                '[data-filler-value]'
              );


            const rowPrice =
              row.querySelector(
                '[data-filler-row-price]'
              );


            const minus =
              row.querySelector(
                '[data-filler-minus]'
              );


            const plus =
              row.querySelector(
                '[data-filler-plus]'
              );


            const renderRow =
              () => {

                const quantity =
                  this.fillerDraft.get(item) ||
                  0;


                value.textContent =
                  quantity;


                rowPrice.textContent =
                  this.formatMoney(
                    quantity *
                    item.fillerPrice
                  );


                minus.disabled =
                  quantity <= 0;


                plus.disabled =
                  quantity >= max;


                this.updateModalSummary();

              };


            minus.addEventListener(
              'click',
              () => {

                const quantity =
                  this.fillerDraft.get(item) ||
                  0;


                this.fillerDraft.set(
                  item,
                  Math.max(
                    0,
                    quantity - 1
                  )
                );


                renderRow();

              }
            );


            plus.addEventListener(
              'click',
              () => {

                const quantity =
                  this.fillerDraft.get(item) ||
                  0;


                this.fillerDraft.set(
                  item,
                  Math.min(
                    max,
                    quantity + 1
                  )
                );


                renderRow();

              }
            );


            this.fillerModalItems.appendChild(
              row
            );


            renderRow();

          }
        );


      this.updateModalSummary();
    }


    async openFillerModal() {

      if (!this.fillerModal) {
        return;
      }


      this.buildFillerModal();


      await this.wait(60);


      this.fillerModal.hidden =
        false;


      this.lockPage();
    }


    closeFillerModal() {

      if (!this.fillerModal) {
        return;
      }


      this.fillerModal.hidden =
        true;


      this.unlockPage();
    }


    async applyFillers() {

      this.fillerDraft.forEach(
        (quantity, item) => {

          item.fillerQuantity =
            quantity;

        }
      );


      this.renderAll();


      await this.wait(100);


      this.closeFillerModal();
    }


    /* ============================================================
       DESKTOP SCROLL HANDOFF
       ============================================================ */

    enableDesktopScrollHandoff() {

      if (!this.productList) {
        return;
      }


      this.productList.addEventListener(
        'wheel',
        (event) => {

          if (
            !window.matchMedia(
              '(min-width: 990px)'
            ).matches
          ) {
            return;
          }


          const list =
            this.productList;


          if (
            list.scrollHeight <=
            list.clientHeight + 1
          ) {
            return;
          }


          const atTop =
            list.scrollTop <= 0;


          const atBottom =
            Math.ceil(
              list.scrollTop +
              list.clientHeight
            ) >=
            list.scrollHeight - 1;


          if (
            (
              atTop &&
              event.deltaY < 0
            ) ||
            (
              atBottom &&
              event.deltaY > 0
            )
          ) {

            event.preventDefault();


            window.scrollBy({
              top: event.deltaY,
              behavior: 'auto'
            });

          }

        },
        {
          passive: false
        }
      );
    }


    /* ============================================================
       STICKY OBSERVER
       ============================================================ */

    setupStickyCartObserver() {

      if (
        !this.stickyCart ||
        !this.addButton ||
        !('IntersectionObserver' in window)
      ) {
        return;
      }


      const observer =
        new IntersectionObserver(
          ([entry]) => {

            const mobile =
              window.matchMedia(
                '(max-width: 749px)'
              ).matches;


            if (!mobile) {

              this.stickyCart.hidden =
                true;

              return;

            }


            this.stickyCart.hidden =
              entry.isIntersecting;

          },
          {
            threshold: 0.15
          }
        );


      observer.observe(
        this.addButton
      );
    }


    /* ============================================================
       CART
       ============================================================ */

    buildCartItems() {

      const variants =
        new Map();


      const add =
        (
          variantId,
          quantity
        ) => {

          if (
            !variantId ||
            quantity <= 0
          ) {
            return;
          }


          const key =
            String(variantId);


          variants.set(
            key,
            (
              variants.get(key) ||
              0
            ) + quantity
          );

        };


      this.items.forEach(
        (item) => {

          if (!item.included) {
            return;
          }


          add(
            item.variantId,
            item.quantity
          );


          if (item.hasFiller) {

            add(
              item.fillerVariantId,
              item.fillerQuantity
            );

          }

        }
      );


      return Array.from(
        variants.entries()
      ).map(
        ([id, quantity]) => ({
          id: Number(id),
          quantity
        })
      );
    }


    async addToCart() {

      const items =
        this.buildCartItems();


      if (!items.length) {
        return;
      }


      const root =
        window.Shopify?.routes?.root ||
        '/';


      const response =
        await fetch(
          `${root}cart/add.js`,
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              'Accept':
                'application/json'
            },

            body:
              JSON.stringify({
                items
              })
          }
        );


      const result =
        await response.json();


      if (!response.ok) {

        throw new Error(
          result?.description ||
          result?.message ||
          'Unable to add the selected pieces.'
        );

      }


      document.dispatchEvent(
        new CustomEvent(
          'cart:refresh',
          {
            bubbles: true,
            detail: result
          }
        )
      );


      document.dispatchEvent(
        new CustomEvent(
          'cart:updated',
          {
            bubbles: true,
            detail: result
          }
        )
      );


      const cartDrawer =
        document.querySelector(
          'cart-drawer'
        );


      if (
        cartDrawer &&
        typeof cartDrawer.open ===
        'function'
      ) {

        cartDrawer.open();

      }


      /*
        No success text.
        The cart drawer acts as confirmation.
      */
    }


    handleAddToCart(button) {

      this.withButtonLoader(
        button,

        async () => {

          try {

            if (this.cartMessage) {

              this.cartMessage.textContent =
                '';

            }


            await this.addToCart();

          } catch (error) {

            console.error(
              'Boho Tales — Shop the Look:',
              error
            );


            if (this.cartMessage) {

              this.cartMessage.textContent =
                error.message ||
                'Something went wrong. Please try again.';

            }

          }

        },

        'Adding'
      );
    }


    /* ============================================================
       EVENTS
       ============================================================ */

    bindEvents() {

      /*
        Quantity / checkbox
      */

      this.items.forEach(
        (item) => {

          const checkbox =
            item.element.querySelector(
              '[data-item-checkbox]'
            );


          const minus =
            item.element.querySelector(
              '[data-quantity-minus]'
            );


          const plus =
            item.element.querySelector(
              '[data-quantity-plus]'
            );


          checkbox?.addEventListener(
            'change',
            () => {

              this.setIncluded(
                item,
                checkbox.checked
              );

            }
          );


          minus?.addEventListener(
            'click',
            () => {

              this.changeQuantity(
                item,
                -1
              );

            }
          );


          plus?.addEventListener(
            'click',
            () => {

              this.changeQuantity(
                item,
                1
              );

            }
          );

        }
      );


      /*
        Filler modal close
      */

      this.root.addEventListener(
        'click',
        (event) => {

          const fillerClose =
            event.target.closest(
              '[data-close-fillers]'
            );


          if (
            fillerClose &&
            this.root.contains(
              fillerClose
            )
          ) {

            event.preventDefault();


            this.closeFillerModal();

          }

        }
      );


      /*
        Customize look
      */

      this.customizeLookButton
        ?.addEventListener(
          'click',
          () => {

            this.withButtonLoader(
              this.customizeLookButton,

              async () => {

                await this.enableCustomizeLook();

              },

              'Customizing'
            );

          }
        );


      /*
        Fillers
      */

      this.fillerToggle
        ?.addEventListener(
          'change',
          () => {

            this.handleFillerToggle();

          }
        );


      this.customizeFillersButton
        ?.addEventListener(
          'click',
          () => {

            this.withButtonLoader(
              this.customizeFillersButton,

              async () => {

                await this.openFillerModal();

              },

              'Opening'
            );

          }
        );


      this.saveFillersButton
        ?.addEventListener(
          'click',
          () => {

            this.withButtonLoader(
              this.saveFillersButton,

              async () => {

                await this.applyFillers();

              },

              'Applying'
            );

          }
        );


      /*
        Cart
      */

      this.addButton
        ?.addEventListener(
          'click',
          () => {

            this.handleAddToCart(
              this.addButton
            );

          }
        );


      this.stickyAddButton
        ?.addEventListener(
          'click',
          () => {

            this.handleAddToCart(
              this.stickyAddButton
            );

          }
        );


      /*
        Escape
      */

      document.addEventListener(
        'keydown',
        (event) => {

          if (
            event.key !== 'Escape'
          ) {
            return;
          }


          if (
            this.fillerModal &&
            !this.fillerModal.hidden
          ) {

            this.closeFillerModal();

          }

        }
      );
    }

  }


  /* ============================================================
     INITIALIZE
     ============================================================ */

  function initializeWithin(
    container = document
  ) {

    const roots = [];


    if (
      container.matches &&
      container.matches(
        ROOT_SELECTOR
      )
    ) {

      roots.push(
        container
      );

    }


    if (
      container.querySelectorAll
    ) {

      roots.push(
        ...container.querySelectorAll(
          ROOT_SELECTOR
        )
      );

    }


    roots.forEach(
      (root) => {

        if (
          root.dataset.bohoShopLookInitialized ===
          'true'
        ) {
          return;
        }


        root.dataset.bohoShopLookInitialized =
          'true';


        new BohoShopTheLook(
          root
        );

      }
    );
  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      () => {

        initializeWithin(
          document
        );

      }
    );

  } else {

    initializeWithin(
      document
    );

  }


  document.addEventListener(
    'shopify:section:load',
    (event) => {

      initializeWithin(
        event.target
      );

    }
  );

})();